import React, { useState, useRef } from 'react';
import api, { setAuthToken, removeAuthToken } from '../services/api';

const MobileUpload = () => {
  const [clientNom, setClientNom] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null);

  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);

    try {
      // Authentification locale (bcrypt + JWT du backend)
      const login = await api.post('/auth/login', {
        email: email.trim(),
        password: password
      });

      const token = login?.data?.access_token;
      if (!login || !login.success || !token) {
        throw new Error(login?.error || 'Identifiants incorrects');
      }

      setAuthToken(token);
      const response = await api.get('/auth/me');
      if (response && response.success && response.role === 'client' && response.client) {
        setClientNom(response.client.nom);
        setClientEmail(response.client.email || email);
        setAuthenticated(true);
      } else {
        throw new Error(response?.error || 'Dossier client introuvable ou désactivé.');
      }
    } catch (err) {
      setLoginError(err.message || 'Identifiants incorrects');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    setAuthenticated(false);
    setClientNom('');
    setClientEmail('');
    setEmail('');
    setPassword('');
    setFile(null);
    setFileMeta(null);
    setPreviewUrl(null);
    setMessage(null);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileMeta({ name: selectedFile.name, size: selectedFile.size, type: selectedFile.type || '' });
      const isImage = (selectedFile.type || '').startsWith('image/');
      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreviewUrl(null);
      }
      setMessage(null);
    }
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(10);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await api.upload('/sync/mobile-upload', formData);

      clearInterval(interval);

      if (response && response.success) {
        setProgress(100);
        setMessage({ type: 'success', text: '🎉 Justificatif envoyé avec succès ! Il apparaîtra sur votre tableau de bord local.' });
        setFile(null);
        setFileMeta(null);
        setPreviewUrl(null);
      } else {
        setMessage({ type: 'error', text: response?.error || response?.detail || 'Erreur lors du dépôt du fichier.' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Impossible de contacter le serveur. Vérifiez votre connexion Wi-Fi.' });
    } finally {
      setUploading(false);
    }
  };

  const renderLogin = () => (
    <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <label style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase' }}>Connexion à votre espace :</label>
      <input
        type="email"
        placeholder="Votre adresse email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '14px',
          color: 'white',
          fontSize: '15px',
          outline: 'none'
        }}
      />
      <input
        type="password"
        placeholder="Votre mot de passe"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '14px',
          color: 'white',
          fontSize: '15px',
          outline: 'none'
        }}
      />
      {loginError && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '12px',
          fontSize: '13px',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid #ef4444',
          color: '#f87171'
        }}>
          {loginError}
        </div>
      )}
      <button type="submit" disabled={loginLoading} style={{
        background: '#1d9e75',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        padding: '14px',
        fontWeight: '600',
        cursor: loginLoading ? 'wait' : 'pointer',
        marginTop: '8px',
        opacity: loginLoading ? 0.7 : 1
      }}>
        {loginLoading ? 'Connexion...' : 'Se connecter'}
      </button>
    </form>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(185deg, #0d1e1a 0%, #070b0a 100%)',
      color: '#e2e8f0',
      fontFamily: 'Inter, system-ui, sans-serif',
      padding: '24px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '24px',
        padding: '24px',
        boxSizing: 'border-box',
        boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <span style={{ fontSize: '48px' }}>📷</span>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '12px 0 4px', color: '#1d9e75' }}>Dépôt Mobile Express</h2>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Photographiez et synchronisez vos justificatifs de frais</p>
        </div>

        {!authenticated ? (
          renderLogin()
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Infos Client connecté */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.02)',
              padding: '12px 16px',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.05)'
            }}>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Client connecté</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#34d399' }}>{clientNom || 'Espace client'}</div>
                {clientEmail && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', wordBreak: 'break-all' }}>{clientEmail}</div>
                )}
              </div>
              <button onClick={handleLogout} style={{
                background: 'transparent',
                border: 'none',
                color: '#ef4444',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px'
              }}>Se déconnecter</button>
            </div>

            {message && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '13px',
                lineHeight: '1.4',
                background: message.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
                color: message.type === 'success' ? '#34d399' : '#f87171'
              }}>
                {message.text}
              </div>
            )}

            {/* Zone photo / dépôt */}
            {!file ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div
                  onClick={() => photoInputRef.current.click()}
                  style={{
                    border: '2px dashed rgba(255, 255, 255, 0.15)',
                    borderRadius: '16px',
                    padding: '32px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'rgba(255, 255, 255, 0.01)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📸</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#f8fafc' }}>Prendre en photo</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>ouvre l'appareil photo</div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <div
                  onClick={() => docInputRef.current.click()}
                  style={{
                    border: '2px dashed rgba(255, 255, 255, 0.15)',
                    borderRadius: '16px',
                    padding: '32px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'rgba(255, 255, 255, 0.01)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#f8fafc' }}>Choisir un document</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>PDF, JPG, PNG depuis vos fichiers</div>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Aperçu" style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '260px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ padding: '40px 20px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)' }}>
                      <div style={{ fontSize: '40px', marginBottom: '8px' }}>📄</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#f8fafc', wordBreak: 'break-all' }}>{fileMeta?.name || file.name}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{((fileMeta?.size || file.size) / 1024).toFixed(1)} Ko</div>
                    </div>
                  )}
                  <button
                    onClick={() => { setFile(null); setFileMeta(null); setPreviewUrl(null); }}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px'
                    }}
                  >✕</button>
                </div>

                {uploading ? (
                  <div style={{ width: '100%' }}>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px', textAlign: 'center' }}>Transmission au cloud... {progress}%</div>
                    <div style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${progress}%`, height: '100%', background: '#1d9e75', borderRadius: '3px', transition: 'width 0.2s' }} />
                    </div>
                  </div>
                ) : (
                  <button onClick={handleUpload} style={{
                    background: '#1d9e75',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer'
                  }}>📤 Envoyer le justificatif</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileUpload;
